// File: frontend/src/pages/LegacyRouteRedirects.js
//
// THE ROUTES THE 2026-08-20 RESTRUCTURE RETIRED, AND WHERE THEY GO NOW.
//
// Five destinations stopped existing. Every one of them is bookmarked by
// somebody, linked from an email, or — in the case of /analytics — written into
// insight cards the backend has been emitting for months and caches for two
// minutes at a time.
//
// A 404 is not an acceptable answer to any of them, and neither is a redirect
// that lands on a page's DEFAULT tab: an operator who followed "inspect failed
// payments" and arrives on a revenue summary will read the revenue summary as
// the answer. So each redirect carries its query string through, and the tabbed
// ones translate the tab as well.
//
//   /analytics?tab=X   → the page that now owns analysis X, on its tab
//   /transactions      → /financials?tab=transactions   (since 10 Sep 2026)
//   /sales             → /financials?tab=sales, or ?tab=transactions — except
//                        /sales?tab=payments, the payments analysis, which stays
//   /remote-access     → /router?tab=remote
//   /routers           → /router
//   /network/cross-site-access → /network/roaming
//
// `replace` throughout: a retired URL should not sit in the back stack waiting
// to bounce the reader forward again the moment they press Back.

import React, { useEffect, useRef } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { openWorkspace } from "../services/handoff";

/**
 * Where an old signed-in Ambassador URL on pesafii.net now lives on
 * ambassador.pesafii.net: the /ambassador or /partner prefix is dropped and the
 * rest (path, query, hash) is kept, so /ambassador/clients?open=12 opens the
 * same client there. Pure, so the whole map is testable for loops.
 */
export function ambassadorPathFor(pathname, search = "", hash = "") {
  const rest = String(pathname || "").replace(/^\/(ambassador|partner)(?=\/|$)/, "") || "/";
  return `${rest.startsWith("/") ? rest : `/${rest}`}${search || ""}${hash || ""}`;
}

/**
 * A cross-APPLICATION move, so a full navigation rather than a router
 * <Navigate>. Signed in here → the one-time handoff signs them in there too;
 * otherwise the Ambassador app asks them to sign in. Fired once, even under
 * React StrictMode's double effects, so a single visit issues a single code.
 */
export function ToAmbassadorApp() {
  const location = useLocation();
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    openWorkspace("ambassador", ambassadorPathFor(location.pathname, location.search, location.hash));
  }, [location]);
  return (
    <Box role="status" aria-live="polite" sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <CircularProgress size={24} />
        <Typography>Opening PesaFii Ambassadors…</Typography>
      </Box>
    </Box>
  );
}

// The five Analytics tabs and where each analysis lives now. `null` means the
// analysis was RETIRED rather than moved — Overview's KPI row and comparison
// chart were a second measurement of what the Router Dashboard already shows,
// so the dashboard is the honest destination for that link.
const ANALYTICS_TAB_DESTINATION = {
  overview:  { path: "/dashboard", tab: null },
  insights:  { path: "/dashboard", tab: null },   // legacy alias
  trends:    { path: "/dashboard", tab: null },   // legacy alias
  customers: { path: "/users",     tab: "insights" },
  packages:  { path: "/packages",  tab: "performance" },
  payments:  { path: "/sales",     tab: "payments" },
  network:   { path: "/router",    tab: "traffic" },
};

/**
 * Rebuilds the query string with the destination's tab substituted, keeping
 * every other parameter (from, to, router_id, and the whole ledger filter
 * vocabulary) exactly as it arrived.
 */
function withTab(search, tab) {
  const p = new URLSearchParams(search);
  if (tab) p.set("tab", tab);
  else p.delete("tab");
  const q = p.toString();
  return q ? `?${q}` : "";
}

export function AnalyticsRedirect() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const dest = ANALYTICS_TAB_DESTINATION[searchParams.get("tab")]
    || ANALYTICS_TAB_DESTINATION.overview;
  return <Navigate to={`${dest.path}${withTab(location.search, dest.tab)}`} replace />;
}

/**
 * A straight move: same query string, new path, optionally landing on a tab.
 * Used for /transactions, /remote-access and /routers, whose parameters (a whole
 * ledger filter set, in the first case) must survive the hop intact.
 */
export function MovedTo({ path, tab = null }) {
  const location = useLocation();
  return <Navigate to={`${path}${withTab(location.search, tab)}`} replace />;
}

/**
 * A pure RENAME: same page, same shape, new address — query string and hash
 * carried through untouched.
 *
 * Deliberately NOT MovedTo, which deletes `tab` when it is given none, because
 * that is correct for a restructure (the destination's tabs differ) and wrong
 * for a rename (nothing about the destination changed). The Partner →
 * Ambassador rename needs this: the Ambassador overview links a recent client
 * as /partner/clients?open=<tenant_id>, and a plain <Navigate to="..."> — which
 * is what these routes used — drops the query, so the link arrived at an
 * unopened list and the client the reader clicked was never shown. Any renamed
 * route whose query carries state (an invite token, a filter, a record to open)
 * belongs here rather than on a bare Navigate.
 */
export function RenamedTo({ path }) {
  const location = useLocation();
  return <Navigate to={`${path}${location.search}${location.hash}`} replace />;
}

/**
 * Bare /settings since Settings became three addresses. The old single page
 * picked its tab from ?tab=, and those links still exist — Wallet's setup gate
 * used to send owners to /settings?tab=wallet-security, and emails and
 * bookmarks do not update themselves. Renaming to /settings/team with the query
 * attached still landed every one of them on Members, so the tab is translated
 * into the section it now names; every other parameter (?focus=…) and the hash
 * come along.
 */
const SETTINGS_TAB_SECTION = {
  "wallet-security": "wallet-security",
  activity: "activity",
  team: "team",
  roles: "team",
};

export function SettingsRedirect() {
  const location = useLocation();
  const p = new URLSearchParams(location.search);
  const tab = p.get("tab");
  const section = SETTINGS_TAB_SECTION[tab] || "team";
  p.delete("tab");
  if (tab === "roles") p.set("view", "roles");
  const q = p.toString();
  return <Navigate to={`/settings/${section}${q ? `?${q}` : ""}${location.hash}`} replace />;
}

/**
 * /sales since Transactions became a Financials tab (10 Sep 2026).
 *
 *   ?tab=payments      stays on this page. The payments analysis (success
 *                      funnel, failures by provider) has no Financials tab and
 *                      is not being moved without a product decision.
 *   ?tab=transactions  → Financials → Transactions, every filter carried through.
 *   anything else      → Financials → Sales, the aggregate sales view.
 *
 * Clicking the old page's own Sales / Transactions tabs therefore lands in the
 * matching Financials tab too — one ledger, one sales view, one address each.
 */
export function SalesRoute({ children }) {
  const location = useLocation();
  const tab = new URLSearchParams(location.search).get("tab");
  if (tab === "payments") return children;
  const dest = tab === "transactions" ? "transactions" : "sales";
  return <Navigate to={`/financials${withTab(location.search, dest)}`} replace />;
}
